import React, { useEffect, useState, useMemo } from "react";
import {
    Box, Paper, Typography, Chip, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Divider, Button,
    Alert, Stack,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import ResizableTable from "../../../component/common/ResizableTable";
import ComboBox from "../../../component/common/ComboBox";
import ActionButton from "../../../component/common/ActionButton";
import PaymentsIcon from "@mui/icons-material/Payments";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { hrApi, fmtKRW, SALARY_STATUS_COLOR } from "../api/hrApi";
import { runApiAction } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";
import TablePaginator, { DEFAULT_PAGE_SIZE, TableSizeSelect } from "../../../component/common/TablePaginator";
import LoadingDots from "../../../component/common/LoadingDots";
import { formatDate, today as getToday } from "../../../util/dateUtils";
import { formatNumberWithCommas, parseNumericInput } from "../../../util/numberFormat";
import FormField from "../../../component/common/FormField";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import { notify } from "../../../util/toast";

const now = getToday();
const today = formatDate(now);
const currentYear = now.year();
const currentMonth = now.month() + 1;

export default function HrSalaryTab() {
    const [year, setYear]   = useState(currentYear);
    const [month, setMonth] = useState(currentMonth);
    const [rows, setRows]   = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sort, setSort] = useState({ key: "employeeName", dir: "asc" });

    // 테이블 페이지네이션 (전체 데이터는 항상 로드, 표시만 페이지 단위)
    const [displayPage, setDisplayPage]         = useState(1);
    const [displayPageSize, setDisplayPageSize] = useState(DEFAULT_PAGE_SIZE);

    // 급여 등록 다이얼로그
    const [addDialog, setAddDialog] = useState(false);
    const [form, setForm] = useState({
        employeeId: "", year: currentYear, month: currentMonth,
        baseSalary: "", allowance: 0, deduction: 0, paymentDate: "", note: "",
    });
    const [salaryDisplay, setSalaryDisplay] = useState({ baseSalary: "", allowance: "", deduction: "" });

    // 지급 기록 다이얼로그
    const [payDialog, setPayDialog] = useState(false);
    const [payTarget, setPayTarget] = useState(null); // 선택된 급여 행
    const [payForm, setPayForm] = useState({ amount: "", paidDate: today, note: "" });
    const [payAmountDisplay, setPayAmountDisplay] = useState("");
    const [paying, setPaying] = useState(false);
    const [allowanceDialog, setAllowanceDialog] = useState(false);
    const [allowanceTarget, setAllowanceTarget] = useState(null);
    const [allowanceForm, setAllowanceForm] = useState({ amount: "", note: "" });
    const [allowanceAmountDisplay, setAllowanceAmountDisplay] = useState("");
    const [savingAllowance, setSavingAllowance] = useState(false);
    const [manageDialog, setManageDialog] = useState(false);
    const [manageTarget, setManageTarget] = useState(null);

    const load = () => {
        setLoading(true);
        Promise.allSettled([
            hrApi.getSalaries(year, month, 0, 9999)
                .then(res => setRows(res?.content ?? (Array.isArray(res) ? res : []))),
            hrApi.getEmployees({ size: 9999 })
                .then(res => setEmployees(res?.content ?? (Array.isArray(res) ? res : []))),
        ]).finally(() => setLoading(false));
        setDisplayPage(1);
    };

    useEffect(() => { load(); }, [year, month]); // eslint-disable-line

    const getDefaultPaymentDate = (targetYear, targetMonth) => {
        const lastDay = new Date(targetYear, targetMonth, 0).getDate();
        const day = String(Math.min(now.date(), lastDay)).padStart(2, "0");
        return `${String(targetYear).padStart(4, "0")}-${String(targetMonth).padStart(2, "0")}-${day}`;
    };

    const getPaymentDateRange = (targetYear, targetMonth) => {
        const monthString = String(targetMonth).padStart(2, "0");
        const firstDay = `${String(targetYear).padStart(4, "0")}-${monthString}-01`;
        const lastDay = `${String(targetYear).padStart(4, "0")}-${monthString}-${String(new Date(targetYear, targetMonth, 0).getDate()).padStart(2, "0")}`;
        return { firstDay, lastDay };
    };

    const openEditDialog = (row) => {
        setForm({
            employeeId: String(row.employeeId),
            year: row.year,
            month: row.month,
            baseSalary: row.baseSalary || "",
            allowance: row.allowance || 0,
            deduction: row.deduction || 0,
            paymentDate: row.paymentDate || getDefaultPaymentDate(row.year, row.month),
            note: row.note || "",
        });
        setSalaryDisplay({
            baseSalary: formatNumberWithCommas(row.baseSalary, { emptyZero: true }),
            allowance: formatNumberWithCommas(row.allowance, { emptyZero: true }),
            deduction: formatNumberWithCommas(row.deduction, { emptyZero: true }),
        });
        setAddDialog(true);
    };

    const openManageDialog = (row) => {
        setManageTarget(row);
        setManageDialog(true);
    };

    // 지급 다이얼로그 열기
    const openPayDialog = (row) => {
        setPayTarget(row);
        const totalOutstanding = row.totalOutstandingAmount || row.remainingAmount || 0;
        setPayForm({ amount: totalOutstanding > 0 ? String(totalOutstanding) : "", paidDate: today, note: "" });
        setPayAmountDisplay(formatNumberWithCommas(totalOutstanding, { emptyZero: true }));
        setPayDialog(true);
    };

    const handleRecordPayment = async () => {
        if (!payTarget || !payForm.amount) return;
        setPaying(true);
        try {
            await hrApi.recordPayment(payTarget.id, {
                amount: Number(payForm.amount),
                paidDate: payForm.paidDate || today,
                note: payForm.note || null,
            });
            setPayDialog(false);
            load();
        } catch (e) {
            notify(e?.response?.data?.message || "지급 기록 실패", "error")
        } finally {
            setPaying(false);
        }
    };

    const openAllowanceDialog = (row) => {
        setAllowanceTarget(row);
        setAllowanceForm({ amount: "", note: "" });
        setAllowanceAmountDisplay("");
        setAllowanceDialog(true);
    };

    const handleAddAllowance = async () => {
        if (!allowanceTarget || !allowanceForm.amount || Number(allowanceForm.amount) <= 0) return;
        setSavingAllowance(true);
        try {
            await hrApi.addAllowance(allowanceTarget.id, {
                amount: Number(allowanceForm.amount),
                note: allowanceForm.note || null,
            });
            setAllowanceDialog(false);
            load();
        } catch (e) {
            notify(e?.response?.data?.message || "수당 추가 실패", "error")
        } finally {
            setSavingAllowance(false);
        }
    };

    const handlePayAll = async () => {
        if (!(await confirmDialog({ message: `${year}년 ${month}월 전체 미지급 급여를 일괄 지급하시겠습니까?` }))) return;
        const result = await hrApi.payAll(year, month);
        notify(`${result.paid}건 지급 완료 처리됐습니다.`, "success")
        load();
    };

    const handleSave = () => runApiAction({
        request: () => hrApi.saveSalary({
            employeeId: Number(form.employeeId),
            year: Number(form.year), month: Number(form.month),
            baseSalary: Number(form.baseSalary),
            allowance: Number(form.allowance),
            deduction: Number(form.deduction),
            paymentDate: form.paymentDate || null,
            note: form.note,
        }),
        onSuccess: () => {
            setForm({ employeeId: "", year: currentYear, month: currentMonth, baseSalary: "", allowance: 0, deduction: 0, paymentDate: "", note: "" });
            setSalaryDisplay({ baseSalary: "", allowance: "", deduction: "" });
            load();
        },
        closeOnSuccess: () => setAddDialog(false),
    });

    const handleSalaryAmountInput = (field, raw) => {
        const value = parseNumericInput(raw);
        setForm(p => ({ ...p, [field]: value === "" ? "" : value }));
        setSalaryDisplay(p => ({ ...p, [field]: formatNumberWithCommas(value, { emptyZero: true }) }));
    };

    const handlePayAmountInput = (raw) => {
        const value = parseNumericInput(raw);
        setPayForm(p => ({ ...p, amount: value === "" ? "" : String(value) }));
        setPayAmountDisplay(formatNumberWithCommas(value, { emptyZero: true }));
    };

    const handleAllowanceAmountInput = (raw) => {
        const value = parseNumericInput(raw);
        setAllowanceForm(p => ({ ...p, amount: value === "" ? "" : String(value) }));
        setAllowanceAmountDisplay(formatNumberWithCommas(value, { emptyZero: true }));
    };

    const handleDelete = async (id) => {
        if (!(await confirmDialog({ message: "이 월의 급여 금액과 지급 기록을 초기화할까요? 급여 행은 남고 금액만 0원으로 바뀝니다." }))) return;
        return runApiAction({
            request: () => hrApi.deleteSalary(id),
            onSuccess: () => { setManageDialog(false); load(); },
        });
    };

    const totalNet       = useMemo(() => rows.reduce((s, r) => s + (r.netSalary  || 0), 0), [rows]);
    const totalPaid      = useMemo(() => rows.reduce((s, r) => s + (r.paidAmount || 0), 0), [rows]);
    const totalRemaining = useMemo(() => totalNet - totalPaid, [totalNet, totalPaid]);
    const totalCarryOver = useMemo(() => rows.reduce((s, r) => s + (r.previousUnpaidAmount || 0), 0), [rows]);
    const totalOutstanding = useMemo(() => rows.reduce((s, r) => s + (r.totalOutstandingAmount || r.remainingAmount || 0), 0), [rows]);
    const sortedRows = useMemo(() => {
        const dir = sort.dir === "asc" ? 1 : -1;
        return [...rows].sort((a, b) => {
            const av = a[sort.key] ?? "";
            const bv = b[sort.key] ?? "";
            if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
            return String(av).localeCompare(String(bv), "ko") * dir;
        });
    }, [rows, sort]);

    const pagedRows = useMemo(
        () => sortedRows.slice((displayPage - 1) * displayPageSize, displayPage * displayPageSize),
        [sortedRows, displayPage, displayPageSize]
    );

    const years  = [currentYear - 1, currentYear];
    const months = Array.from({ length: 12 }, (_, i) => i + 1);

    // 현재 선택된 연월 표 기준의 지급 필요 건수.
    // 기존에는 전체 overdue API와 현재월 rows를 섞어 표시해서 화면의 건수와 경고 건수가 어긋났다.
    const paymentRequiredRows = useMemo(
        () => rows.filter(r => (r.totalOutstandingAmount || r.remainingAmount || 0) > 0),
        [rows]
    );
    const unpaidCount = paymentRequiredRows.length;
    const unpaidAmount = useMemo(
        () => paymentRequiredRows.reduce((s, r) => s + (r.totalOutstandingAmount || r.remainingAmount || 0), 0),
        [paymentRequiredRows]
    );
    const overdueRows = useMemo(
        () => paymentRequiredRows.filter(r => r.status === "OVERDUE"),
        [paymentRequiredRows]
    );
    const partialRows = useMemo(
        () => paymentRequiredRows.filter(r => r.status === "PARTIAL"),
        [paymentRequiredRows]
    );
    const toggleSort = (key) => {
        setSort((prev) => ({
            key,
            dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
        }));
    };
    const sortLabel = (key, label) => `${label}${sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : " ⇅"}`;
    const paymentDateRange = getPaymentDateRange(Number(form.year), Number(form.month));

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

            {/* 미지급 경고 */}
            {unpaidCount > 0 && (
                <Alert severity="error" icon={<PaymentsIcon />}>
                    <span>미지급/잔여 {unpaidCount}건 — {fmtKRW(unpaidAmount)}</span>
                    {(overdueRows.length > 0 || partialRows.length > 0) && (
                        <span>
                            {" "}
                            ({overdueRows.length > 0 ? `미지급 상태 ${overdueRows.length}건` : ""}
                            {overdueRows.length > 0 && partialRows.length > 0 ? " · " : ""}
                            {partialRows.length > 0 ? `일부지급 ${partialRows.length}건` : ""})
                        </span>
                    )}
                </Alert>
            )}

            <Paper sx={ds.card}>
                {/* 헤더 */}
                <Box sx={{ px: 2.5, pt: 2, pb: 1.5, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Typography sx={{ ...ds.typography.sectionTitle, flex: 1 }}>급여 관리</Typography>
                    <Box sx={{ width: 90 }}>
                        <ComboBox clearable={false} placeholder="연도"
                            options={years.map(y => ({ value: y, label: `${y}년` }))}
                            value={year}
                            onChange={v => setYear(Number(v))} />
                    </Box>
                    <Box sx={{ width: 80 }}>
                        <ComboBox clearable={false} placeholder="월"
                            options={months.map(m => ({ value: m, label: `${m}월` }))}
                            value={month}
                            onChange={v => setMonth(Number(v))} />
                    </Box>
                    {/* N개씩 보기 — 표 우측 상단 고정(공용 컨트롤) */}
                    <TableSizeSelect value={displayPageSize} onChange={v => { setDisplayPageSize(v); setDisplayPage(1); }} />
                    <ActionButton variant="primary"
                        startIcon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                        onClick={handlePayAll}
                        label="전체 일괄 지급" />
                </Box>
                <Divider />

                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("employeeName")}>{sortLabel("employeeName", "이름")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                            <TableCell align="right" sx={{ color: "#0f172a", cursor: "pointer" }} onClick={() => toggleSort("baseSalary")}>{sortLabel("baseSalary", "기본급")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer" }} onClick={() => toggleSort("allowance")}>{sortLabel("allowance", "수당")}</TableCell>
                            <TableCell align="right" sx={{  cursor: "pointer" }} onClick={() => toggleSort("deduction")}>{sortLabel("deduction", "공제")}</TableCell>
                            <TableCell align="right" sx={{  fontWeight: 700, cursor: "pointer" }} onClick={() => toggleSort("netSalary")}>{sortLabel("netSalary", "실수령액")}</TableCell>
                            <TableCell align="right" sx={{  fontWeight: 700, cursor: "pointer" }} onClick={() => toggleSort("paidAmount")}>{sortLabel("paidAmount", "지급액")}</TableCell>
                            <TableCell align="right" sx={{  fontWeight: 700, cursor: "pointer" }} onClick={() => toggleSort("remainingAmount")}>{sortLabel("remainingAmount", "잔여액")}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, cursor: "pointer" }} onClick={() => toggleSort("previousUnpaidAmount")}>{sortLabel("previousUnpaidAmount", "이월액")}</TableCell>
                            <TableCell align="right" sx={{ color: "#2563eb", fontWeight: 700, cursor: "pointer" }} onClick={() => toggleSort("totalOutstandingAmount")}>{sortLabel("totalOutstandingAmount", "총 지급 필요액")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("statusLabel")}>{sortLabel("statusLabel", "상태")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("paymentDate")}>{sortLabel("paymentDate", "지급예정일")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("note")}>{sortLabel("note", "메모")}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pagedRows.map(r => {
                            const sc = SALARY_STATUS_COLOR[r.status] || SALARY_STATUS_COLOR.SCHEDULED;
                            const totalOutstandingAmount = r.totalOutstandingAmount || 0;
                            const isPaid = totalOutstandingAmount <= 0;
                            return (
                                <TableRow key={r.id} hover sx={{ cursor: "pointer" }}
                                    onClick={() => openManageDialog(r)}>
                                    <TableCell sx={{ fontWeight: 600 }}>{r.employeeName}</TableCell>
                                    <TableCell>{r.deptName || "—"}</TableCell>
                                    <TableCell align="right">{fmtKRW(r.baseSalary)}</TableCell>
                                    <TableCell align="right" sx={{ color: "#16a34a" }}>
                                        {r.allowance > 0 ? `+${fmtKRW(r.allowance)}` : "—"}
                                    </TableCell>
                                    <TableCell align="right" sx={{ color: "#dc2626" }}>
                                        {r.deduction > 0 ? `-${fmtKRW(r.deduction)}` : "—"}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: "#2563eb" }}>
                                        {fmtKRW(r.netSalary)}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: "#16a34a" }}>
                                        {r.paidAmount > 0 ? fmtKRW(r.paidAmount) : "—"}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: r.remainingAmount > 0 ? "#dc2626" : "#94a3b8" }}>
                                        {r.remainingAmount > 0 ? fmtKRW(r.remainingAmount) : "—"}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: r.previousUnpaidAmount > 0 ? "#d97706" : "#94a3b8" }}>
                                        {r.previousUnpaidAmount > 0 ? fmtKRW(r.previousUnpaidAmount) : "—"}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: (r.totalOutstandingAmount || 0) > 0 ? "#2563eb" : "#94a3b8" }}>
                                        {(r.totalOutstandingAmount || 0) > 0 ? fmtKRW(r.totalOutstandingAmount) : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={sc.label} size="small"
                                            sx={{ bgcolor: sc.bg, color: sc.color, fontWeight: 600 }} />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>
                                        {r.paymentDate ? r.paymentDate.slice(5) : "—"}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{r.note || "—"}</TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>합계</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#2563eb" }}>{fmtKRW(totalNet)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: "#16a34a" }}>{fmtKRW(totalPaid)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: totalRemaining > 0 ? "#dc2626" : "#94a3b8" }}>
                                    {totalRemaining > 0 ? fmtKRW(totalRemaining) : "—"}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: totalCarryOver > 0 ? "#d97706" : "#94a3b8" }}>
                                    {totalCarryOver > 0 ? fmtKRW(totalCarryOver) : "—"}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: totalOutstanding > 0 ? "#2563eb" : "#94a3b8" }}>
                                    {totalOutstanding > 0 ? fmtKRW(totalOutstanding) : "—"}
                                </TableCell>
                                <TableCell colSpan={3} />
                            </TableRow>
                        )}
                    </TableBody>
                </ResizableTable>}
                {!loading && <TablePaginator
                    total={rows.length}
                    pageSize={displayPageSize}
                    page={displayPage}
                    onPageChange={setDisplayPage}
                />}
            </Paper>

            {/* ── 급여 등록 다이얼로그 ── */}
            <FormModal
                open={addDialog}
                onClose={() => setAddDialog(false)}
                title="급여 수정"
                maxWidth="xs"
                onSave={handleSave}
                saveDisabled={!form.employeeId || !form.baseSalary}
                footerExtras={
                    <ActionButton variant="secondary" startIcon={false}
                        onClick={() => { setAddDialog(false); setManageDialog(true); }}
                        label="이전" />
                }
            >
                <Stack spacing={2}>
                    <FormField label="직원" required>
                        <ComboBox clearable={false} placeholder="직원"
                            options={employees.map(e => ({ value: e.id, label: `${e.name}${e.deptName ? ` (${e.deptName})` : ""}` }))}
                            value={form.employeeId}
                            onChange={v => setForm(p => ({ ...p, employeeId: v }))} />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <FormField label="연도" required sx={{ flex: 1 }}>
                            <TextField size="small" type="number" value={form.year}
                                onChange={e => setForm(p => ({ ...p, year: Number(e.target.value) }))} />
                        </FormField>
                        <FormField label="월" required sx={{ width: 90 }}>
                            <TextField size="small" type="number" value={form.month}
                                onChange={e => setForm(p => ({ ...p, month: Number(e.target.value) }))} />
                        </FormField>
                    </Box>
                    <FormField label="기본급 (원)" required>
                        <TextField size="small" value={salaryDisplay.baseSalary}
                            onChange={e => handleSalaryAmountInput("baseSalary", e.target.value)}
                            inputProps={{ inputMode: "numeric" }} />
                    </FormField>
                    <FormField label="수당 (원)">
                        <TextField size="small" value={salaryDisplay.allowance}
                            onChange={e => handleSalaryAmountInput("allowance", e.target.value)}
                            inputProps={{ inputMode: "numeric" }} />
                    </FormField>
                    <FormField label="공제 (원)">
                        <TextField size="small" value={salaryDisplay.deduction}
                            onChange={e => handleSalaryAmountInput("deduction", e.target.value)}
                            inputProps={{ inputMode: "numeric" }} />
                    </FormField>
                    {form.baseSalary && (
                        <Typography sx={{ fontSize: 13, color: "#2563eb", fontWeight: 700 }}>
                            실수령액: {fmtKRW(Number(form.baseSalary) + Number(form.allowance) - Number(form.deduction))}
                        </Typography>
                    )}
                    <FormField label="지급 예정일">
                        <TextField size="small" type="date" value={form.paymentDate}
                            onChange={e => setForm(p => ({ ...p, paymentDate: e.target.value }))}
                            inputProps={{ min: paymentDateRange.firstDay, max: paymentDateRange.lastDay }} />
                    </FormField>
                    <FormField label="메모">
                        <TextField size="small" fullWidth value={form.note}
                            onChange={e => setForm(p => ({ ...p, note: e.target.value }))} />
                    </FormField>
                </Stack>
            </FormModal>

            {/* ── 지급 기록 다이얼로그 ── */}
            <FormModal
                open={payDialog}
                onClose={() => setPayDialog(false)}
                title={`${payTarget?.employeeName || ""} — 지급 기록`}
                maxWidth="sm"
                onSave={handleRecordPayment}
                saving={paying}
                saveLabel={paying ? "처리 중..." : "지급 기록"}
                saveDisabled={!payForm.amount || Number(payForm.amount) <= 0}
                cancelLabel="닫기"
                footerExtras={
                    <ActionButton variant="secondary" startIcon={false}
                        onClick={() => { setPayDialog(false); setManageDialog(true); }}
                        label="이전" />
                }
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {payTarget && (
                        <>
                            {/* 요약 */}
                            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                                <SummaryChip label="실수령액" value={fmtKRW(payTarget.netSalary)} color="#2563eb" />
                                <SummaryChip label="현재월 지급액" value={fmtKRW(payTarget.paidAmount || 0)} color="#16a34a" />
                                <SummaryChip label="이월액" value={fmtKRW(payTarget.previousUnpaidAmount || 0)} color="#d97706" />
                                <SummaryChip label="총 지급 필요액" value={fmtKRW(payTarget.totalOutstandingAmount || payTarget.remainingAmount || 0)} color="#2563eb" />
                            </Box>

                            {/* 지급 이력 */}
                            {Array.isArray(payTarget.payments) && payTarget.payments.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748b", mb: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <HistoryIcon sx={{ fontSize: 14 }} /> 지급 이력
                                    </Typography>
                                    <ResizableTable size="small" sx={ds.tableMiniSx}>
                                        <TableHead>
                                            <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                                <TableCell>지급일</TableCell>
                                                <TableCell align="right">금액</TableCell>
                                                <TableCell>담당자</TableCell>
                                                <TableCell>메모</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {payTarget.payments.map(p => (
                                                <TableRow key={p.id}>
                                                    <TableCell>{p.paidDate}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 600, color: "#16a34a" }}>{fmtKRW(p.amount)}</TableCell>
                                                    <TableCell>{p.paidByName || "—"}</TableCell>
                                                    <TableCell sx={{ color: "#64748b" }}>{p.note || "—"}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </ResizableTable>
                                </Box>
                            )}

                            <Divider />

                            {/* 이번 지급 입력 */}
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>이번 지급</Typography>
                            <Alert severity="info" sx={{ py: 0.5, fontSize: 12 }}>
                                입력한 지급액은 이전 미지급 급여부터 오래된 월 순서대로 먼저 차감됩니다.
                            </Alert>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                                <FormField label="지급 금액 (원)" required sx={{ flex: 1 }}>
                                    <TextField size="small"
                                        value={payAmountDisplay}
                                        onChange={e => handlePayAmountInput(e.target.value)}
                                        inputProps={{ inputMode: "numeric" }} />
                                </FormField>
                                <ActionButton variant="secondary" startIcon={false}
                                    onClick={() => handlePayAmountInput(String(payTarget.totalOutstandingAmount || payTarget.remainingAmount || 0))}
                                    label="총액 전부" />
                                <ActionButton variant="secondary" startIcon={false}
                                    onClick={() => handlePayAmountInput(String(Math.floor((payTarget.totalOutstandingAmount || payTarget.remainingAmount || 0) / 2)))}
                                    label="절반" />
                            </Box>
                            <FormField label="지급일" required>
                                <TextField size="small" type="date" value={payForm.paidDate}
                                    onChange={e => setPayForm(p => ({ ...p, paidDate: e.target.value }))} />
                            </FormField>
                            <FormField label="메모">
                                <TextField size="small" value={payForm.note}
                                    onChange={e => setPayForm(p => ({ ...p, note: e.target.value }))} />
                            </FormField>

                            {payForm.amount && (
                                <Alert severity="info" sx={{ py: 0.5, fontSize: 12 }}>
                                    이번 지급 후 총 잔여: {fmtKRW(Math.max(0, (payTarget.totalOutstandingAmount || payTarget.remainingAmount || 0) - Number(payForm.amount)))}
                                </Alert>
                            )}
                        </>
                    )}
                </Box>
            </FormModal>

            <FormModal
                open={manageDialog}
                onClose={() => setManageDialog(false)}
                title={`${manageTarget?.employeeName || ""} — 급여 관리`}
                maxWidth="xs"
                cancelLabel="닫기"
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {manageTarget && (
                        <>
                            <Alert severity="info" sx={{ py: 0.5, fontSize: 12 }}>
                                {manageTarget.year}년 {manageTarget.month}월 · 실수령액 {fmtKRW(manageTarget.netSalary || 0)}
                                {" / "}
                                총 지급 필요액 {fmtKRW(manageTarget.totalOutstandingAmount || manageTarget.remainingAmount || 0)}
                            </Alert>
                            <Stack spacing={1}>
                                <ActionButton variant="secondary"
                                    startIcon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                                    disabled={(manageTarget.totalOutstandingAmount || 0) <= 0}
                                    onClick={() => { setManageDialog(false); openPayDialog(manageTarget); }}
                                    label={manageTarget.totalOutstandingAmount > 0 ? "급여지급" : "지급완료"} />
                                <ActionButton variant="edit"
                                    onClick={() => { setManageDialog(false); openEditDialog(manageTarget); }} />
                                <ActionButton variant="secondary" startIcon={false}
                                    onClick={() => { setManageDialog(false); openAllowanceDialog(manageTarget); }}
                                    label="수당"
                                    sx={{ color: "#d97706", borderColor: "#fbbf24",
                                        "&:hover": { color: "#d97706", borderColor: "#fbbf24", bgcolor: "#fef3c7" } }} />
                                <ActionButton variant="delete"
                                    onClick={() => handleDelete(manageTarget.id)}
                                    label="초기화" />
                            </Stack>
                        </>
                    )}
                </Box>
            </FormModal>

            <FormModal
                open={allowanceDialog}
                onClose={() => setAllowanceDialog(false)}
                title={`${allowanceTarget?.employeeName || ""} — 수당 추가`}
                maxWidth="xs"
                onSave={handleAddAllowance}
                saving={savingAllowance}
                saveLabel={savingAllowance ? "저장 중..." : "수당 추가"}
                saveDisabled={!allowanceForm.amount || Number(allowanceForm.amount) <= 0}
                footerExtras={
                    <ActionButton variant="secondary" startIcon={false}
                        onClick={() => { setAllowanceDialog(false); setManageDialog(true); }}
                        sx={{ color: "#64748b", borderColor: "transparent",
                            "&:hover": { borderColor: "transparent", bgcolor: "#f1f5f9", color: "#64748b" } }}
                        label="이전" />
                }
            >
                <Stack spacing={2}>
                    {allowanceTarget && (
                        <Alert severity="info" sx={{ py: 0.5, fontSize: 12 }}>
                            현재 수당 {fmtKRW(allowanceTarget.allowance || 0)} / 실수령액 {fmtKRW(allowanceTarget.netSalary || 0)}
                        </Alert>
                    )}
                    <FormField label="추가 수당 금액 (원)" required>
                        <TextField
                            size="small"
                            value={allowanceAmountDisplay}
                            onChange={e => handleAllowanceAmountInput(e.target.value)}
                            inputProps={{ inputMode: "numeric" }}
                        />
                    </FormField>
                    <FormField label="사유 / 항목">
                        <TextField
                            size="small"
                            value={allowanceForm.note}
                            onChange={e => setAllowanceForm(p => ({ ...p, note: e.target.value }))}
                            placeholder="예: 야근수당, 식대, 성과수당"
                        />
                    </FormField>
                    {allowanceTarget && allowanceForm.amount && Number(allowanceForm.amount) > 0 && (
                        <Alert severity="warning" sx={{ py: 0.5, fontSize: 12 }}>
                            반영 후 수당 {fmtKRW((allowanceTarget.allowance || 0) + Number(allowanceForm.amount))}
                            {" / "}
                            실수령액 {fmtKRW((allowanceTarget.netSalary || 0) + Number(allowanceForm.amount))}
                        </Alert>
                    )}
                </Stack>
            </FormModal>
        </Box>
    );
}

function SummaryChip({ label, value, color }) {
    return (
        <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 1.5, px: 1.5, py: 0.8, flex: 1, minWidth: 110 }}>
            <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{label}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color }}>{value}</Typography>
        </Box>
    );
}
